<script lang="ts">
  import axios from "axios";
  import CircularCrossIcon from "../utils/assets/circular_cross_icon.svelte";
  import StarIcon from "../utils/assets/star_icon.svelte";
  import { scopedLocalStorage, scopedSessionStorage } from "../utils/storage";
  import {
    is_feedback_form_open,
    is_feedback_collected,
    client_id,
  } from "../stores/chat";
  import { BASE_URL } from "../utils/constants";
  import { AssetLoader } from "../utils/assetLoader";
  import type {
    FeedbackFormQuestion,
    FeedbackFormTexts,
  } from "../types/globalTypes";

  export let accentColor = "#007AFF";
  export let onStartNewChat: () => void;
  export let feedback_form_config: FeedbackFormTexts;
  export let cdnUrl = "";

  let showThankYou = false;

  $: isAllRated = feedback_form_config?.questions
    ?.filter((question: FeedbackFormQuestion) => question?.type === "rating")
    ?.every((question: FeedbackFormQuestion) => question?.answer !== "");

  $: if ($is_feedback_form_open) {
    setTimeout(() => {
      document.getElementById("vfwidget")?.classList.add("show-feedback-modal");
    }, 100);
  }

  $: {
    if (!cdnUrl) {
      const assetLoader = AssetLoader?.getInstance();
      cdnUrl = assetLoader?.getCDNUrl();
    }
  }

  function onClose() {
    document
      .getElementById("vfwidget")
      ?.classList.remove("show-feedback-modal");
    setTimeout(() => {
      showThankYou = false;
      is_feedback_form_open.set(false);
      scopedSessionStorage?.setItem("is_feedback_form_open", "false");
    }, 1000);
  }

  function handleInputChange(e: Event, questionIndex: number) {
    const target = e.target as HTMLTextAreaElement;
    if (feedback_form_config?.questions?.[questionIndex]) {
      feedback_form_config.questions[questionIndex].answer = target?.value;
    }
  }

  async function skipFeedback() {
    is_feedback_collected.set(true);
    const user_id = scopedLocalStorage.getItem("user_id");
    const session_id = JSON.parse(
      scopedLocalStorage.getItem("session_info") || "{}"
    )?.id;
    await axios.post(`${BASE_URL}/fe/update_feedback_status`, {
      user_id,
      session_id,
    });
  }

  async function submitFeedback(e: Event) {
    e?.preventDefault();
    showThankYou = true;
    const session_id = JSON.parse(
      scopedLocalStorage.getItem("session_info") || "{}"
    )?.id;
    const user_id = scopedLocalStorage.getItem("user_id");
    try {
      if (user_id && session_id) {
        await axios.post(`${BASE_URL}/fe/feedback/${$client_id}`, {
          user_id,
          session_id,
          feedback: feedback_form_config?.questions?.map(
            (question: FeedbackFormQuestion) => ({
              questionnaire: {
                question: question?.question,
                type: question?.type,
              },
              answer: question?.answer?.toString(),
            })
          ),
        });
        is_feedback_collected.set(true);
      } else {
        await skipFeedback();
      }
    } catch {
      await skipFeedback();
    }
  }

  function startNewChat() {
    onClose();
    onStartNewChat();
  }

  async function handleSkip() {
    onClose();
    await skipFeedback();
  }
</script>

{#if $is_feedback_form_open && feedback_form_config?.questions?.length > 0}
  <div
    class="verbaflo-feedback-form-container-wrapper"
    on:click={async () => {
      onClose();
      await skipFeedback();
    }}
    on:keydown={() => {}}
    tabindex="0"
    role="button"
    aria-label="Close feedback form"
  >
    <div
      class="verbaflo-feedback-form-container"
      on:click={(e) => e?.stopPropagation()}
      on:keydown={() => {}}
      tabindex="0"
      role="button"
      aria-label="Feedback form container"
    >
      {#if showThankYou}
        <img
          src={`${cdnUrl}/assets/gifs/completion-check.gif`}
          alt="Thank you for your feedback"
          class="completion-check-gif"
        />
        <h2 class="verbaflo-thank-you-title">
          {feedback_form_config?.headers?.submission_header}
        </h2>
        <p class="verbaflo-thank-you-subtitle">
          {feedback_form_config?.headers?.submission_sub_header}
        </p>

        <button
          class="verbaflo-new-chat-btn"
          style="--accent-color: {accentColor};"
          on:click={startNewChat}
        >
          {feedback_form_config?.headers?.submit_button}
        </button>

        <button class="verbaflo-close-btn" on:click={onClose}>
          {feedback_form_config?.headers?.skip_button}
        </button>
      {:else}
        <div class="verbaflo-feedback-header">
          <h2 class="verbaflo-feedback-title">
            {feedback_form_config?.headers?.header}
          </h2>
          <button class="verbaflo-feedback-close-btn" on:click={handleSkip}>
            <CircularCrossIcon />
          </button>
        </div>

        <p class="verbaflo-feedback-subtitle">
          {feedback_form_config?.headers?.sub_header}
        </p>

        {#each feedback_form_config?.questions as question, questionIndex}
          <div class="verbaflo-feedback-question">
            <span
              class="verbaflo-feedback-question-number"
              style="--accent-color: {accentColor};">{questionIndex + 1}</span
            >
            {#if question?.type === "input"}
              <div class="verbaflo-feedback-question-content">
                <p class="verbaflo-feedback-question-text">
                  {question?.question}
                </p>
                <textarea
                  class="verbaflo-feedback-question-input"
                  placeholder={question?.placeholder ||
                    "Please type your feedback"}
                  bind:value={question.answer}
                  on:change={(e) => handleInputChange(e, questionIndex)}
                  on:keydown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && isAllRated) {
                      e.preventDefault();
                      submitFeedback(e);
                    }
                  }}
                />
              </div>
            {:else if question?.type === "rating"}
              <div>
                <p class="verbaflo-feedback-question-text">
                  {question?.question}
                </p>
                <div class="verbaflo-feedback-stars">
                  {#each Array(5) as _, starIndex}
                    <button
                      class="verbaflo-feedback-star"
                      class:selected={Number(question?.answer) > starIndex}
                      on:click={() => {
                        question.answer = starIndex + 1;
                      }}
                    >
                      <StarIcon
                        selected={Number(question?.answer) > starIndex}
                        selectedColor={accentColor}
                      />
                    </button>
                  {/each}
                </div>
              </div>
            {/if}
          </div>
        {/each}

        <button
          class="verbaflo-feedback-submit-btn"
          style="--accent-color: {accentColor}; --disabled-color: {accentColor ===
          '#FACC15'
            ? 'white'
            : '#F1F1F1'};"
          on:click={submitFeedback}
          disabled={!isAllRated}
        >
          {feedback_form_config?.headers?.feedback_submit_button}
        </button>
        <button class="verbaflo-feedback-skip-btn" on:click={handleSkip}>
          {feedback_form_config?.headers?.feedback_skip_button}
        </button>
      {/if}
    </div>
  </div>
{/if}

<style>
  :global(#vfwidget .verbaflo-feedback-form-container-wrapper) {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    width: 100% !important;
    height: 100% !important;
    z-index: 99999 !important;
    background-color: rgba(0, 0, 0, 0.6) !important;
    border-radius: 0 0 24px 24px !important;
    overflow: hidden !important;
    transition: all 0.5s ease-in-out !important;
    opacity: 0 !important;
    pointer-events: none !important;
  }

  :global(
      #vfwidget.show-feedback-modal .verbaflo-feedback-form-container-wrapper
    ) {
    opacity: 1 !important;
    pointer-events: all !important;
  }

  :global(#vfwidget .verbaflo-feedback-form-container) {
    position: absolute !important;
    left: 50% !important;
    transform: translateX(-50%) !important;
    width: calc(100% - 16px) !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 16px !important;
    background-color: white !important;
    border-radius: 12px !important;
    margin: 0 !important;
    border: 1px solid #e0e0e0 !important;
    transition: all 0.5s ease-in-out !important;
    bottom: -110% !important;
  }

  :global(#vfwidget.show-feedback-modal .verbaflo-feedback-form-container) {
    bottom: 8px !important;
  }

  @media only screen and (max-width: 767px) and (orientation: portrait),
    only screen and (max-height: 500px) and (orientation: landscape) {
    :global(#vfwidget .verbaflo-feedback-form-container-wrapper) {
      border-radius: 0 !important;
    }

    :global(#vfwidget .verbaflo-feedback-form-container) {
      width: 100% !important;
      border-radius: 12px 12px 0px 0px !important;
      margin: 0 !important;
      border: none !important;
    }

    :global(#vfwidget.show-feedback-modal .verbaflo-feedback-form-container) {
      bottom: 0 !important;
    }
  }

  :global(#vfwidget .verbaflo-feedback-header) {
    display: flex !important;
    justify-content: space-between !important;
    align-items: center !important;
    margin-bottom: 6px !important;
  }

  :global(#vfwidget .verbaflo-feedback-title) {
    font-size: 16px !important;
    font-weight: 500 !important;
    color: #000000 !important;
    line-height: 24px !important;
    text-transform: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  :global(#vfwidget .verbaflo-feedback-close-btn) {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
    cursor: pointer !important;
  }

  :global(#vfwidget .verbaflo-feedback-close-btn:hover) {
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-feedback-close-btn:active) {
    text-decoration: none !important;
  }
  :global(#vfwidget .verbaflo-feedback-subtitle) {
    font-size: 14px !important;
    color: #535862 !important;
    text-transform: none !important;
    line-height: 20px !important;
    margin: 0 0 16px 0 !important;
    padding: 0 !important;
  }

  :global(#vfwidget .verbaflo-feedback-question-content) {
    width: 100% !important;
    padding: 0 16px 0 0 !important;
  }

  :global(#vfwidget .verbaflo-feedback-question) {
    display: flex !important;
    gap: 6px !important;
    margin-bottom: 14px !important;
  }

  :global(#vfwidget .verbaflo-feedback-question-number) {
    width: 23px !important;
    height: 23px !important;
    background-color: var(--accent-color) !important;
    color: white !important;
    border-radius: 100% !important;
    display: flex !important;
    flex-shrink: 0 !important;
    align-items: center !important;
    justify-content: center !important;
    font-size: 12px !important;
    font-weight: 700 !important;
    line-height: 20px !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  :global(#vfwidget .verbaflo-feedback-question-text) {
    font-size: 14px !important;
    font-weight: 500 !important;
    color: #000000 !important;
    text-transform: none !important;
    line-height: 20px !important;
    margin: 0 0 12px 0 !important;
    padding: 0 !important;
  }

  :global(#vfwidget .verbaflo-feedback-question-input) {
    font-size: 14px !important;
    font-weight: 400 !important;
    border: 1px solid #d0d5dd !important;
    border-radius: 8px !important;
    padding: 6px 10px !important;
    width: 100% !important;
    outline: none !important;
    box-shadow: none !important;
    text-transform: none !important;
    width: 100% !important;
    height: 66px !important;
    resize: none !important;
  }

  :global(#vfwidget .verbaflo-feedback-question-input::placeholder) {
    color: #a4a7ae !important;
  }

  :global(#vfwidget .verbaflo-feedback-stars) {
    display: flex !important;
    gap: 24px !important;
  }

  :global(#vfwidget .verbaflo-feedback-star) {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
    background-color: transparent !important;
    padding: 0 !important;
    margin: 0 !important;
    cursor: pointer !important;
  }

  :global(#vfwidget .verbaflo-feedback-star:hover) {
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-feedback-star:active) {
    text-decoration: none !important;
  }
  :global(#vfwidget .verbaflo-feedback-submit-btn) {
    background-color: var(--accent-color) !important;
    color: white !important;
    border: none !important;
    border-radius: 8px !important;
    padding: 10px !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    margin-bottom: 10px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  :global(#vfwidget .verbaflo-feedback-submit-btn:hover) {
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-feedback-submit-btn:active) {
    text-decoration: none !important;
  }
  :global(#vfwidget .verbaflo-feedback-submit-btn:disabled) {
    cursor: not-allowed !important;
    opacity: 0.55 !important;
    color: var(--disabled-color) !important;
  }

  :global(#vfwidget .verbaflo-feedback-skip-btn) {
    background: transparent !important;
    border: none !important;
    color: #000000 !important;
    font-size: 14px !important;
    cursor: pointer !important;
    text-transform: none !important;
    margin: 0 auto !important;
  }

  :global(#vfwidget .verbaflo-feedback-skip-btn:hover) {
    text-decoration: underline !important;
  }

  :global(#vfwidget .verbaflo-thank-you-title) {
    font-size: 16px !important;
    font-weight: 500 !important;
    line-height: 24px !important;
    text-transform: none !important;
    text-align: center !important;
    color: #000000 !important;
    margin: 0 0 4px 0 !important;
    padding: 0 !important;
  }

  :global(#vfwidget .verbaflo-thank-you-subtitle) {
    font-size: 14px !important;
    color: #535862 !important;
    line-height: 20px !important;
    text-transform: none !important;
    text-align: center !important;
    margin: 0 0 16px 0 !important;
    padding: 0 !important;
  }

  :global(#vfwidget .verbaflo-new-chat-btn) {
    background-color: var(--accent-color) !important;
    color: white !important;
    border-radius: 8px !important;
    border: transparent !important;
    padding: 10px !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    margin-bottom: 16px !important;
    width: auto !important;
  }

  :global(#vfwidget .verbaflo-new-chat-btn:hover) {
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-new-chat-btn:active) {
    text-decoration: none !important;
  }
  :global(#vfwidget .verbaflo-close-btn) {
    background: transparent !important;
    border: none !important;
    color: #000000 !important;
    font-size: 14px !important;
    cursor: pointer !important;
    text-transform: none !important;
    margin: 0 auto 35px auto !important;
  }

  :global(#vfwidget .verbaflo-close-btn:active) {
    text-decoration: none !important;
  }

  :global(#vfwidget .verbaflo-close-btn:hover) {
    text-decoration: underline !important;
  }

  :global(#vfwidget .completion-check-gif) {
    width: 130px !important;
    height: 130px !important;
    object-fit: contain !important;
    margin: 5px auto -10px auto !important;
  }
</style>
